import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import type { WeddingEventRow } from "@wedding/shared";
import { useActiveEvent, useEventName } from "../../lib/wedding-event";

interface CountdownSectionProps {
  /** Ham düğün satırı — hedef etkinliği bölüm kendisi seçer (en yakın gelecek). */
  wedding: WeddingEventRow | null | undefined;
}

function useCountdown(target: Date) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    mins: Math.floor((diff % 3_600_000) / 60_000),
    secs: Math.floor((diff % 60_000) / 1_000),
    isPast: diff === 0,
  };
}

export function CountdownSection({ wedding }: CountdownSectionProps) {
  const { t } = useTranslation();
  const eventName = useEventName();
  // Kına geçene kadar kınaya, sonra otomatik düğüne sayar.
  const active = useActiveEvent(wedding);
  const target = new Date(active?.dateTime ?? 0);
  const { days, hours, mins, secs, isPast } = useCountdown(target);
  const sectionRef = useRef<HTMLElement>(null);
  const numbersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current || !numbersRef.current) return;
    gsap.fromTo(
      numbersRef.current.children,
      { y: 60, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );
  }, []);

  if (!active || isPast) return null;

  const units = [
    { value: days, label: "GÜN" },
    { value: hours, label: "SAAT" },
    { value: mins, label: "DAKİKA" },
    { value: secs, label: "SANİYE" },
  ];

  return (
    <section
      ref={sectionRef}
      className="flex min-h-screen flex-col items-center justify-center bg-[#1b1817] px-6 py-24"
    >
      <p className="mb-12 text-xs font-medium tracking-[0.4em] text-white/40 uppercase">
        {t("home.countdown.labelFor", { event: eventName(active) })}
      </p>

      <div ref={numbersRef} className="flex gap-6 sm:gap-12 md:gap-16">
        {units.map(({ value, label }) => (
          <div key={label} className="flex flex-col items-center gap-3">
            <span
              className="font-serif text-6xl font-bold tabular-nums text-white sm:text-7xl md:text-8xl lg:text-9xl"
              style={{ fontFamily: "var(--font-serif, 'Playfair Display', serif)" }}
            >
              {String(value).padStart(2, "0")}
            </span>
            <span className="text-[10px] font-medium tracking-[0.3em] text-white/40 uppercase sm:text-xs">
              {label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
